import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { ResizablePanelGroup, ResizablePanel, ResizableHandle } from "@/components/ui/resizable";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator, DropdownMenuLabel } from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useAuth } from "@/context/AuthContext";
import { useChatRoulette } from "@/hooks/useChatRoulette";
import VideoTile from "@/components/VideoTile";
import ReportDialog from "@/components/ReportDialog";
import TrialBanner from "@/components/TrialBanner";
import MaintenanceNotice from "@/components/MaintenanceNotice";
import Profile from "@/pages/Profile";
import Privacy from "@/pages/Privacy";
import api, { BRAND } from "@/lib/appApi";
import { COUNTRIES } from "@/lib/countries";
import { Flag } from "@/components/Flag";
import { GENDERS, GENDER_LABELS, GenderIcon } from "@/lib/genders";
import { toast } from "sonner";
import { Play, SkipForward, Square, Send, Flag as FlagIcon, Crown, Lock, Users, LogOut, Shield, Check, ShieldAlert, Video, VideoOff, Mic, MicOff, User, Globe, Search } from "lucide-react";

export default function Chat() {
  const navigate = useNavigate();
  const { user, logout, setUser, detectCountry } = useAuth();
  const rc = useChatRoulette();
  const [input, setInput] = useState("");
  const [reportOpen, setReportOpen] = useState(false);
  const [overlay, setOverlay] = useState(null);
  const [screenGuard, setScreenGuard] = useState(false);
  const [siteMaintenance, setSiteMaintenance] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const scrollRef = useRef(null);
  const premium = user?.is_premium;
  const isAdmin = user?.is_admin;

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 767px)");
    const update = () => setIsMobile(mq.matches);
    update();
    mq.addEventListener("change", update);
    return () => mq.removeEventListener("change", update);
  }, []);

  useEffect(() => {
    api.get("/config").then((r) => setSiteMaintenance(!!r.data.maintenance_mode)).catch(() => {});
  }, []);

  useEffect(() => {
    if (user && !user.country && detectCountry) detectCountry(user);
  }, [user, detectCountry]);

  useEffect(() => {
    if (isAdmin) return;
    let t;
    const trigger = (msg) => {
      setScreenGuard(true);
      clearTimeout(t);
      t = setTimeout(() => setScreenGuard(false), 1500);
      if (msg) toast.warning(msg);
    };
    const onKey = (e) => {
      if (e.key === "PrintScreen" || (e.metaKey && e.shiftKey) || (e.ctrlKey && e.shiftKey && ["S", "s"].includes(e.key))) {
        trigger("Screenshots and screen recording are not allowed here.");
      }
    };
    window.addEventListener("keyup", onKey);
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("keyup", onKey);
      window.removeEventListener("keydown", onKey);
      clearTimeout(t);
    };
  }, [isAdmin]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [rc.messages]);

  useEffect(() => { if (rc.error) toast.error(rc.error); }, [rc.error]);

  const sendMessage = (e) => {
    e.preventDefault();
    if (!input.trim() || rc.status !== "connected") return;
    rc.sendChat(input);
    setInput("");
  };

  const savePrefs = async (patch) => {
    try {
      const res = await api.put("/profile", patch);
      setUser(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    }
  };

  const active = rc.status === "searching" || rc.status === "connected";
  const partnerBadges = rc.partner ? (
    <>
      {rc.partner.gender && (
        <span className="px-1.5 py-0.5 rounded-md text-xs glass border border-border flex items-center gap-1" data-testid="partner-gender">
          <GenderIcon gender={rc.partner.gender} /> {GENDER_LABELS[rc.partner.gender]}
        </span>
      )}
      {rc.partner.country ? (
        <span className="px-1.5 py-0.5 rounded-md text-xs glass border border-border flex items-center gap-1" data-testid="partner-flag">
          <Flag code={rc.partner.country} size={16} /> {rc.partner.country}
        </span>
      ) : rc.partner.country_hidden ? (
        <span className="px-1.5 py-0.5 rounded-md text-xs glass border border-border text-muted-foreground flex items-center gap-1" data-testid="partner-country-private">
          <Globe className="w-3 h-3" />Private
        </span>
      ) : null}
      {rc.partner.is_premium && (
        <span className="px-1.5 py-0.5 rounded-md text-xs brand-gradient-bg text-white flex items-center gap-0.5" data-testid="partner-premium-badge">
          <Crown className="w-3 h-3" /> Premium
        </span>
      )}
    </>
  ) : null;

  if (!isAdmin && (siteMaintenance || rc.maintenance)) {
    return <MaintenanceNotice />;
  }

  return (
    <div className="h-screen flex flex-col bg-background text-foreground overflow-hidden">
      <TrialBanner />
      <header className="shrink-0 flex items-center justify-between gap-3 px-4 py-2.5 border-b border-border">
        <div className="flex items-center gap-2 shrink-0 cursor-pointer" onClick={() => navigate("/")}>
          <img src={BRAND.logo} alt="logo" className="w-8 h-8 rounded-full" />
          <span className="font-heading font-extrabold hidden sm:block">Random Cam <span className="brand-gradient-text">Chat</span></span>
        </div>

        <div className="flex items-center gap-2">
          {rc.status === "idle" && (
            <Button onClick={rc.start} data-testid="start-btn" className="brand-gradient-bg text-white border-0 rounded-full px-6 h-10 font-semibold transition-transform hover:scale-105">
              <Play className="w-4 h-4 mr-1.5" /> Start
            </Button>
          )}
          {active && (
            <>
              <Button onClick={rc.next} data-testid="next-btn" className="brand-gradient-bg text-white border-0 rounded-full px-6 h-10 font-semibold transition-transform hover:scale-105">
                <SkipForward className="w-4 h-4 mr-1.5" /> Next
              </Button>
              <Button onClick={rc.stop} variant="outline" data-testid="stop-btn" className="rounded-full px-5 h-10 border-border bg-secondary hover:bg-accent">
                <Square className="w-4 h-4 mr-1.5" /> Stop
              </Button>
              {rc.localStream && (
                <div className="flex items-center gap-1.5 ml-1">
                  <Button size="icon" variant="outline" onClick={rc.toggleCam} data-testid="toggle-cam-btn"
                    className={`rounded-full h-10 w-10 border-border ${rc.camOn ? "bg-secondary" : "bg-destructive/20 border-destructive"}`}>
                    {rc.camOn ? <Video className="w-4 h-4" /> : <VideoOff className="w-4 h-4 text-destructive" />}
                  </Button>
                  <Button size="icon" variant="outline" onClick={rc.toggleMic} data-testid="toggle-mic-btn"
                    className={`rounded-full h-10 w-10 border-border ${rc.micOn ? "bg-secondary" : "bg-destructive/20 border-destructive"}`}>
                    {rc.micOn ? <Mic className="w-4 h-4" /> : <MicOff className="w-4 h-4 text-destructive" />}
                  </Button>
                </div>
              )}
            </>
          )}
        </div>

        <div className="flex items-center gap-2 shrink-0">
          <FilterControls premium={premium} user={user} onSave={savePrefs} navigate={navigate} />
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button data-testid="account-menu-btn" className="rounded-full outline-none">
                <Avatar className="w-9 h-9 border border-border">
                  <AvatarImage src={user?.picture} />
                  <AvatarFallback className="bg-secondary text-xs">{user?.name?.[0] || "U"}</AvatarFallback>
                </Avatar>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="bg-card border-border w-56">
              <DropdownMenuLabel className="flex flex-col">
                <span className="font-medium truncate">{user?.name}</span>
                <span className="text-xs text-muted-foreground truncate">{user?.email}</span>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => setOverlay("profile")} data-testid="menu-profile"><User className="w-4 h-4 mr-2" /> Profile & settings</DropdownMenuItem>
              {premium ? (
                <div className="px-2 py-1.5 text-xs flex items-center gap-2 text-amber-400"><Crown className="w-3.5 h-3.5" /> Premium active</div>
              ) : (
                <DropdownMenuItem onClick={() => navigate("/pricing")} data-testid="menu-upgrade" className="text-amber-400"><Crown className="w-4 h-4 mr-2" /> Upgrade to Premium</DropdownMenuItem>
              )}
              {user?.is_admin && <DropdownMenuItem onClick={() => navigate("/admin")} data-testid="menu-admin"><Shield className="w-4 h-4 mr-2" /> Admin panel</DropdownMenuItem>}
              <DropdownMenuItem onClick={() => setOverlay("legal")} data-testid="menu-privacy">Privacy & Terms</DropdownMenuItem>
              <DropdownMenuItem onClick={logout} data-testid="menu-logout"><LogOut className="w-4 h-4 mr-2" /> Log out</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>

      {(() => {
        const partnerTile = (
          <VideoTile stream={rc.remoteStream} label="Stranger" badges={partnerBadges} fit="cover"
            searching={rc.status === "searching"} placeholder="Press Start to meet someone" audioControls />
        );
        const ownTile = <VideoTile stream={rc.localStream} muted fit="contain" bare cornerLabel="You" placeholder="Your camera is off" />;
        const chatPanel = (
          <div className="h-full flex flex-col bg-card/40">
            <div className="shrink-0 flex items-center justify-between px-4 py-2 border-b border-border">
              <span className="font-heading font-bold text-sm">Chat</span>
              {rc.status === "connected" && (
                <Button size="sm" variant="ghost" onClick={() => setReportOpen(true)} data-testid="report-btn" className="text-destructive hover:text-destructive hover:bg-destructive/10 h-8">
                  <FlagIcon className="w-4 h-4 mr-1" /> Report
                </Button>
              )}
            </div>
            <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-3 space-y-2">
              {rc.messages.length === 0 && (
                <div className="h-full flex items-center justify-center text-center text-sm text-muted-foreground px-6">
                  {rc.status === "connected" ? "You're connected! Say hi 👋" : "Messages will appear here once you're matched."}
                </div>
              )}
              {rc.messages.map((m, i) => (
                <div key={i} className={`flex ${m.from === "me" ? "justify-end" : "justify-start"}`}>
                  <div data-testid="chat-message" className={`max-w-[80%] px-3 py-2 rounded-2xl text-sm ${m.from === "me" ? "brand-gradient-bg text-white rounded-br-sm" : "bg-secondary text-foreground rounded-bl-sm"}`}>
                    <span className="block text-[10px] opacity-70 mb-0.5">{m.from === "me" ? "You" : "Stranger"}</span>
                    {m.text}
                  </div>
                </div>
              ))}
            </div>
            <form onSubmit={sendMessage} className="shrink-0 p-2.5 border-t border-border flex gap-2">
              <Input value={input} onChange={(e) => setInput(e.target.value)} placeholder={rc.status === "connected" ? "Type a message…" : "Connect to start chatting"} disabled={rc.status !== "connected"} data-testid="chat-input" className="bg-secondary border-border rounded-full" />
              <Button type="submit" disabled={rc.status !== "connected" || !input.trim()} data-testid="chat-send-btn" className="brand-gradient-bg text-white border-0 rounded-full w-11 h-10 p-0 shrink-0">
                <Send className="w-4 h-4" />
              </Button>
            </form>
          </div>
        );

        return isMobile ? (
          <div className="flex-1 flex flex-col min-h-0" data-testid="mobile-layout">
            <div className="flex-1 min-h-0 p-1.5 pb-0">{partnerTile}</div>
            <div className="h-[26%] min-h-0 p-1.5 pb-0">{ownTile}</div>
            <div className="h-[34%] min-h-0 mt-1.5 border-t border-border">{chatPanel}</div>
          </div>
        ) : (
          <ResizablePanelGroup direction="horizontal" className="flex-1">
            <ResizablePanel defaultSize={58} minSize={35} maxSize={60}>
              <div className="h-full p-3 flex flex-col gap-3">
                <div className="w-full aspect-video max-h-[93%] shrink-0">{partnerTile}</div>
                <div className="flex-1 min-h-0">{ownTile}</div>
              </div>
            </ResizablePanel>
            <ResizableHandle withHandle data-testid="resize-handle" />
            <ResizablePanel defaultSize={42} minSize={22}>{chatPanel}</ResizablePanel>
          </ResizablePanelGroup>
        );
      })()}

      {screenGuard && !isAdmin && (
        <div className="fixed inset-0 z-[60] bg-black flex flex-col items-center justify-center text-center px-8 gap-3" data-testid="screen-guard">
          <ShieldAlert className="w-14 h-14 text-destructive" />
          <p className="font-heading font-bold text-xl text-white">Recording is disabled</p>
          <p className="text-sm text-zinc-300 max-w-sm">Screenshots and screen recording aren't allowed to protect members. Video will return in a moment.</p>
        </div>
      )}

      <ReportDialog open={reportOpen} onOpenChange={setReportOpen} reportedUserId={rc.partner?.user_id} roomId={rc.roomId} remoteStream={rc.remoteStream} />
      {user && !user.gender && <GenderOnboarding onSave={savePrefs} />}
      {overlay === "profile" && <Profile asOverlay onClose={() => setOverlay(null)} />}
      {overlay === "legal" && <Privacy asOverlay onClose={() => setOverlay(null)} />}
    </div>
  );
}

function FilterControls({ premium, user, onSave, navigate }) {
  const [countrySearch, setCountrySearch] = useState("");
  const prefs = user?.preferences || [];
  const prefCountries = user?.preferred_countries || [];

  const togglePref = (g) => {
    const next = prefs.includes(g) ? prefs.filter((x) => x !== g) : [...prefs, g];
    onSave({ preferences: next });
  };
  const toggleCountry = (code) => {
    const next = prefCountries.includes(code) ? prefCountries.filter((x) => x !== code) : [...prefCountries, code];
    onSave({ preferred_countries: next });
  };

  const filtered = COUNTRIES.filter((c) => c.name.toLowerCase().includes(countrySearch.toLowerCase()));

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline" data-testid="filters-btn" className="rounded-full h-9 border-border bg-secondary hover:bg-accent gap-1.5">
          <Users className="w-4 h-4" />
          <span className="hidden md:inline text-sm">Filters</span>
          {!premium && <Lock className="w-3 h-3 text-amber-400" />}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="bg-card border-border w-80">
        {!premium ? (
          <div className="text-center py-2">
            <Crown className="w-8 h-8 mx-auto text-amber-400 mb-2" />
            <p className="font-heading font-bold">Premium feature</p>
            <p className="text-sm text-muted-foreground mt-1 mb-4">Filter who you meet by gender and country with a Premium subscription.</p>
            <Button onClick={() => navigate("/pricing")} data-testid="filters-upgrade-btn" className="brand-gradient-bg text-white border-0 rounded-full w-full">Upgrade now</Button>
          </div>
        ) : (
          <div className="space-y-4">
            <div>
              <p className="text-xs font-semibold text-muted-foreground uppercase mb-2 flex items-center gap-1"><Users className="w-3 h-3" /> Match by gender (select any)</p>
              <div className="grid grid-cols-2 gap-1.5">
                {GENDERS.map((g) => (
                  <button key={g.v} onClick={() => togglePref(g.v)} data-testid={`pref-${g.v}`} className={`text-xs px-2 py-1.5 rounded-lg border transition-colors flex items-center justify-center gap-1 ${prefs.includes(g.v) ? "border-primary bg-primary/15" : "border-border bg-secondary hover:bg-accent"}`}>
                    <GenderIcon gender={g.v} className="w-3 h-3" /> {g.l} {prefs.includes(g.v) && <Check className="w-3 h-3 text-primary" />}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <p className="text-xs font-semibold text-muted-foreground uppercase mb-2 flex items-center gap-1"><Globe className="w-3 h-3" /> Match by country</p>
              <div className="relative mb-2">
                <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input value={countrySearch} onChange={(e) => setCountrySearch(e.target.value)} placeholder="Search countries…" data-testid="country-search" className="bg-secondary border-border h-8 pl-8 text-sm" />
              </div>
              {prefCountries.length > 0 && (
                <div className="flex flex-wrap gap-1 mb-2">
                  {prefCountries.map((c) => (
                    <button key={c} onClick={() => toggleCountry(c)} className="text-xs px-2 py-0.5 rounded-md brand-gradient-bg text-white flex items-center gap-1">
                      <Flag code={c} size={14} /> {c} <span className="opacity-70">×</span>
                    </button>
                  ))}
                </div>
              )}
              <div className="max-h-40 overflow-y-auto space-y-1">
                {filtered.map((c) => {
                  const on = prefCountries.includes(c.code);
                  return (
                    <button key={c.code} onClick={() => toggleCountry(c.code)} data-testid={`country-${c.code}`} className={`w-full flex items-center justify-between text-sm px-3 py-1.5 rounded-lg border transition-colors ${on ? "border-primary bg-primary/15" : "border-border bg-secondary hover:bg-accent"}`}>
                      <span className="flex items-center gap-2"><Flag code={c.code} size={16} /> {c.name}</span>
                      {on && <Check className="w-4 h-4 text-primary" />}
                    </button>
                  );
                })}
              </div>
              <p className="text-xs text-muted-foreground pt-2">Leave empty to match with everyone.</p>
            </div>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}

function GenderOnboarding({ onSave }) {
  const [saving, setSaving] = useState(false);
  const pick = async (g) => {
    setSaving(true);
    await onSave({ gender: g });
    setSaving(false);
  };
  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 glass" data-testid="gender-onboarding">
      <div className="w-full max-w-md rounded-2xl border border-border bg-card p-8 text-center shadow-2xl">
        <img src={BRAND.logo} alt="logo" className="w-14 h-14 rounded-full mx-auto mb-4" />
        <h2 className="font-heading text-2xl font-extrabold">One quick step</h2>
        <p className="text-sm text-muted-foreground mt-2 mb-6">Please select your gender to start chatting. You can change it later in your profile.</p>
        <div className="grid grid-cols-1 gap-2">
          {GENDERS.map((g) => (
            <button key={g.v} disabled={saving} onClick={() => pick(g.v)} data-testid={`onboard-gender-${g.v}`}
              className="flex items-center gap-3 px-4 py-3 rounded-xl border border-border bg-secondary hover:bg-accent hover:border-primary transition-colors disabled:opacity-50">
              <GenderIcon gender={g.v} className="w-4 h-4 text-primary" /> <span className="font-medium">{g.l}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
